import { useState } from 'react';
import VerifiedHistory from '../profile/VerifiedHistory.jsx';
import { formatDisplayName } from '../../p2p/displayName.js';
import { trustedHelloNickname, trustedPeerNickname } from '../../security/input.js';
import {
  clearStoredIdentity,
  exportIdentityMnemonicPhrase,
  importIdentityMnemonicPhrase
} from '../../p2p/identity.js';
import ReleaseNote from '../../ui/ReleaseNote.jsx';
import BlurredRecoveryPhrase from '../../ui/BlurredRecoveryPhrase.jsx';
import {
  clearRecoveryBackupAcknowledged,
  markRecoveryBackupAcknowledged
} from '../../p2p/recoveryBackup.js';
import { COUNTRIES, countryFlag } from '../../p2p/countries.js';
import { clearOwnDisplayIdentity, getPlayerProfile } from '../../p2p/playerProfiles.js';
import {
  challengeJoinUrl,
  formatChallengeLabel
} from '../../p2p/challenges.js';

function savedCalibration() {
  try {
    return JSON.parse(localStorage.getItem('dechess:vdf-calibration:v1'));
  } catch {
    return null;
  }
}

export default function AccountNetwork({
  identity,
  nickname,
  nicknameDraft,
  setNicknameDraft,
  saveNickname,
  country,
  countryDraft,
  setCountryDraft,
  saveCountry,
  inviteUrl,
  copyInvite,
  bootstrapDraft,
  setBootstrapDraft,
  connectToBootstrap,
  remotePeers,
  connectionStats,
  sendHello,
  joined,
  messages,
  scope,
  signalingCount,
  notice,
  ownCertificateShard,
  archivist,
  setArchivistPreference,
  visibleArchivistCount = 0,
  archivistFloor = 8,
  archivistCeiling = 12,
  constrainedClient = false,
  gossipCount,
  openToChallenges,
  onToggleOpenToChallenges,
  blockedChallengers = [],
  onUnblockChallenger,
  incomingChallenges = [],
  onAcceptChallenge,
  onDeclineChallenge,
  pendingOutgoingChallenge = null,
  onOpenPendingOutgoingBoard,
  onCancelPendingOutgoingChallenge
}) {
  const calibration = savedCalibration();
  const [recoveryPhrase, setRecoveryPhrase] = useState('');
  const [importPhrase, setImportPhrase] = useState('');
  const [identityNotice, setIdentityNotice] = useState('');
  const [identityBusy, setIdentityBusy] = useState(false);
  const [showRecoveryPhrase, setShowRecoveryPhrase] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);

  async function revealRecoveryPhrase() {
    setIdentityBusy(true);
    setIdentityNotice('');
    try {
      const phrase = await exportIdentityMnemonicPhrase();
      setRecoveryPhrase(phrase);
      setShowRecoveryPhrase(true);
      setIdentityNotice('Recovery phrase revealed. Store it offline before switching browsers.');
    } catch (error) {
      setIdentityNotice(error.message);
    } finally {
      setIdentityBusy(false);
    }
  }

  async function copyRecoveryPhrase() {
    if (!recoveryPhrase) {
      return;
    }
    await navigator.clipboard.writeText(recoveryPhrase);
    setIdentityNotice('Recovery phrase copied to clipboard.');
  }

  async function restoreRecoveryPhrase(event) {
    event.preventDefault();
    setIdentityBusy(true);
    setIdentityNotice('');
    try {
      const restored = await importIdentityMnemonicPhrase(importPhrase);
      markRecoveryBackupAcknowledged(restored.playerId);
      setIdentityNotice(`Restored ${restored.playerId}. Reloading…`);
      window.setTimeout(() => window.location.reload(), 600);
    } catch (error) {
      setIdentityNotice(error.message);
      setIdentityBusy(false);
    }
  }

  async function removeIdentity() {
    if (!confirmRemove) {
      setConfirmRemove(true);
      setIdentityNotice(
        'Export your recovery phrase first. Removing the identity cannot be undone.'
      );
      return;
    }

    setIdentityBusy(true);
    setIdentityNotice('');
    try {
      await clearStoredIdentity();
      clearRecoveryBackupAcknowledged(identity.playerId);
      clearOwnDisplayIdentity(identity.playerId);
      window.location.reload();
    } catch (error) {
      setIdentityNotice(error.message);
      setIdentityBusy(false);
      setConfirmRemove(false);
    }
  }

  return (
    <div className="account-layout">
      <section className="panel account-intro">
        <div>
          <p className="eyebrow">Local account</p>
          <h2>
            <span className="country-flag" aria-hidden="true">{countryFlag(country)}</span>
            {' '}
            {formatDisplayName(nickname, identity.playerId)}
          </h2>
          <p className="muted">
            Your identity belongs to this browser. There is no registration,
            email address, or central account database.
          </p>
        </div>
        <span className="privacy-badge">Stored on this device</span>
      </section>

      <VerifiedHistory playerId={identity.playerId} />

      <div className="dashboard">
        <section className="panel">
          <p className="eyebrow">Profile</p>
          <h2>Identity details</h2>
          <dl className="facts">
            <div>
              <dt>Player ID</dt>
              <dd>{identity.playerId}</dd>
            </div>
            <div>
              <dt>Peer session</dt>
              <dd>{identity.peerId}</dd>
            </div>
          </dl>
          <form className="stack" onSubmit={saveNickname}>
            <label htmlFor="nickname">Display name</label>
            <p className="muted identity-tag-note">
              A fixed 7-character identity tag from your player ID is shown with
              your name, for example Alice (5984abc).
            </p>
            <div className="input-row">
              <input
                id="nickname"
                maxLength="32"
                value={nicknameDraft}
                onChange={(event) => setNicknameDraft(event.target.value)}
              />
              <button type="submit" className="secondary">Save</button>
            </div>
          </form>
          <form className="stack country-form" onSubmit={saveCountry}>
            <label htmlFor="country">Country flag</label>
            <p className="muted identity-tag-note">
              Shown on the local leaderboard and shared with lobby peers.
              Pick “Intergalactic” if you prefer no nation-state.
            </p>
            <div className="input-row">
              <select
                id="country"
                value={countryDraft}
                onChange={(event) => setCountryDraft(event.target.value)}
              >
                <option value="">🏳️ No flag yet</option>
                {COUNTRIES.map((entry) => (
                  <option key={entry.code} value={entry.code}>
                    {entry.flag} {entry.name}
                  </option>
                ))}
              </select>
              <button type="submit" className="secondary">Save flag</button>
            </div>
          </form>
        </section>

        <section className="panel identity-portable-panel">
          <p className="eyebrow">Portable identity</p>
          <h2>Recovery phrase</h2>
          <p className="recovery-warning" role="alert">
            <span className="recovery-warning-mark" aria-hidden="true">⚠️</span>
            <span>
              Never share this phrase. Anyone who has it can take your player
              identity, ratings, and certificates. If you lose it, this identity
              cannot be recovered.
            </span>
          </p>
          <p className="muted">
            Export your private keys as a 48-word dictionary phrase. Store it
            offline, on paper or in a password manager you trust.
          </p>
          <div className="identity-actions">
            <button
              type="button"
              className="secondary"
              onClick={revealRecoveryPhrase}
              disabled={identityBusy}
            >
              {showRecoveryPhrase ? 'Refresh recovery phrase' : 'Show recovery phrase'}
            </button>
            {showRecoveryPhrase && (
              <button
                type="button"
                onClick={copyRecoveryPhrase}
                disabled={identityBusy || !recoveryPhrase}
              >
                Copy phrase
              </button>
            )}
          </div>
          {showRecoveryPhrase && (
            <BlurredRecoveryPhrase value={recoveryPhrase} />
          )}
          <form className="stack" onSubmit={restoreRecoveryPhrase}>
            <label htmlFor="import-phrase">Restore from recovery phrase</label>
            <textarea
              id="import-phrase"
              className="recovery-import"
              rows={4}
              placeholder="Enter all 48 words separated by spaces"
              value={importPhrase}
              onChange={(event) => setImportPhrase(event.target.value)}
            />
            <button type="submit" className="secondary" disabled={identityBusy}>
              Import identity
            </button>
          </form>
          <div className="danger-zone">
            <h3>Remove identity from this browser</h3>
            <p className="muted">
              This deletes your local private keys, custom display name, and
              country flag. Export your recovery phrase first or you will lose
              access to this player ID on this device.
            </p>
            <button
              type="button"
              className="danger-button"
              onClick={removeIdentity}
              disabled={identityBusy}
            >
              {confirmRemove ? 'Confirm remove identity' : 'Remove identity'}
            </button>
          </div>
          {identityNotice && (
            <p className="identity-notice" role="status">{identityNotice}</p>
          )}
        </section>

        <section className="panel incoming-challenges" id="incoming-challenges" aria-label="Challenge inbox">
          <p className="eyebrow">Challenge inbox</p>
          <h2>Accept or decline</h2>
          <p className="muted">
            Incoming lobby challenges also appear in chat. Accept opens the
            board in this tab.
          </p>

          {pendingOutgoingChallenge?.challenge && (
            <div className="challenge-card outgoing" role="status">
              <div className="challenge-copy">
                <strong>Outgoing challenge</strong>
                <span>
                  {formatChallengeLabel({
                    timeControl: pendingOutgoingChallenge.challenge.timeControl,
                    variant: pendingOutgoingChallenge.challenge.variant,
                    rated: pendingOutgoingChallenge.challenge.rated
                  })}
                  {' · waiting for '}
                  {formatDisplayName(
                    getPlayerProfile(pendingOutgoingChallenge.targetPlayerId)?.nickname || 'Player',
                    pendingOutgoingChallenge.targetPlayerId
                  )}
                </span>
              </div>
              <div className="challenge-actions">
                <button
                  type="button"
                  className="large-cta"
                  onClick={() => onOpenPendingOutgoingBoard?.()}
                >
                  Open my board
                </button>
                <button
                  type="button"
                  className="text-button"
                  onClick={() => onCancelPendingOutgoingChallenge?.()}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {incomingChallenges.length === 0 ? (
            <p className="muted">No incoming challenges right now.</p>
          ) : (
            <ul className="challenge-list">
              {incomingChallenges.map((challenge) => {
                const profile = getPlayerProfile(challenge.challenger.playerId);
                const name = formatDisplayName(
                  challenge.challenger.nickname || profile?.nickname || 'Player',
                  challenge.challenger.playerId
                );
                const flag = countryFlag(
                  challenge.challenger.country || profile?.country || ''
                );
                return (
                  <li key={challenge.challengeId} className="challenge-card">
                    <div className="challenge-copy">
                      <strong>
                        <span className="country-flag" aria-hidden="true">{flag}</span>
                        {' '}
                        {name}
                      </strong>
                      <span>
                        {formatChallengeLabel({
                          timeControl: challenge.timeControl,
                          variant: challenge.variant,
                          rated: challenge.rated
                        })}
                      </span>
                      <a
                        className="challenge-accept-link"
                        href={challengeJoinUrl(challenge)}
                        onClick={(event) => {
                          event.preventDefault();
                          onAcceptChallenge?.(challenge);
                        }}
                      >
                        Open accept link
                      </a>
                    </div>
                    <div className="challenge-actions">
                      <button
                        type="button"
                        className="large-cta"
                        onClick={() => onAcceptChallenge?.(challenge)}
                      >
                        Accept
                      </button>
                      <button
                        type="button"
                        className="text-button"
                        onClick={() => onDeclineChallenge?.(challenge)}
                      >
                        Decline
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <section className="panel" id="challenge-preferences">
          <p className="eyebrow">Challenge preferences</p>
          <h2>Ladder challenges</h2>
          <p className="muted">
            Choose whether other players can challenge you directly from the
            lobby chat, Online now banner, or leaderboard while you are in the lobby.
            Turn this off to block all incoming challenges and avoid spam.
            You can also block individual players; manage that list below.
          </p>
          <div
            className="challenge-preference-options"
            role="radiogroup"
            aria-label="Accept ladder challenges"
          >
            <label className="settings-toggle challenge-preference-option">
              <input
                type="radio"
                name="challenge-preference"
                checked={openToChallenges}
                onChange={() => onToggleOpenToChallenges(true)}
              />
              <span>
                <strong>Accept incoming ladder challenges</strong>
                <small>Yes — show challenges on the Play tab and allow direct invites.</small>
              </span>
            </label>
            <label className="settings-toggle challenge-preference-option">
              <input
                type="radio"
                name="challenge-preference"
                checked={!openToChallenges}
                onChange={() => onToggleOpenToChallenges(false)}
              />
              <span>
                <strong>Do not accept ladder challenges</strong>
                <small>No — ignore incoming leaderboard challenges for this player ID.</small>
              </span>
            </label>
          </div>

          <div className="blocked-challengers">
            <h3>Blocked players</h3>
            <p className="muted">
              Challenges from these player IDs are ignored. Unblock to allow them again.
            </p>
            {blockedChallengers.length === 0 ? (
              <p className="muted blocked-challengers-empty">No blocked players yet.</p>
            ) : (
              <ul className="blocked-challengers-list">
                {blockedChallengers.map((entry) => {
                  const profile = getPlayerProfile(entry.playerId);
                  const label = formatDisplayName(
                    entry.nickname || profile?.nickname || 'Player',
                    entry.playerId
                  );
                  return (
                    <li key={entry.playerId}>
                      <div>
                        <strong>{label}</strong>
                        <small className="muted">{entry.playerId}</small>
                      </div>
                      <button
                        type="button"
                        className="secondary"
                        onClick={() => onUnblockChallenger?.(entry.playerId)}
                      >
                        Unblock
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </section>

        <section className="panel">
          <p className="eyebrow">Availability</p>
          <h2>Archivist mode</h2>
          <p className="muted">
            Donate storage and bandwidth by verifying and re-serving every
            certificate shard (all 256). Automatic mode helps until the lobby
            has enough archivists, then stands down. Archivists cannot alter
            ratings or proofs.
          </p>
          <fieldset className="settings-archivist-mode">
            <legend className="sr-only">Archivist preference</legend>
            <label className="settings-toggle">
              <input
                type="radio"
                name="archivist-preference"
                checked={(archivist.preference || 'auto') === 'auto'}
                onChange={() => setArchivistPreference?.('auto')}
              />
              <span>Automatic (help until the network has enough)</span>
            </label>
            <label className="settings-toggle">
              <input
                type="radio"
                name="archivist-preference"
                checked={archivist.preference === 'on'}
                onChange={() => setArchivistPreference?.('on')}
              />
              <span>Always on</span>
            </label>
            <label className="settings-toggle">
              <input
                type="radio"
                name="archivist-preference"
                checked={archivist.preference === 'off'}
                onChange={() => setArchivistPreference?.('off')}
              />
              <span>Always off</span>
            </label>
          </fieldset>
          <p className="muted" role="status">
            Visible archivists: {visibleArchivistCount}
            {' · '}
            target {archivistFloor}–{archivistCeiling}
            {' · '}
            mode: {archivist.preference || 'auto'}
            {archivist.enabled ? ' (active)' : ' (idle)'}
            {constrainedClient && (archivist.preference || 'auto') === 'auto'
              ? ' · phones stay opt-in only'
              : ''}
          </p>
          {archivist.enabled && (
            <p className="muted" role="status">
              Covering all certificate shards — rotating active rooms while
              keeping the full ladder archive locally.
            </p>
          )}
          <dl className="facts">
            <div>
              <dt>Identity shard</dt>
              <dd>{ownCertificateShard}</dd>
            </div>
            <div>
              <dt>Archive coverage</dt>
              <dd>{archivist.enabled ? '256 / 256 shards' : 'Own shard + lobby peers'}</dd>
            </div>
            <div>
              <dt>Certificates received</dt>
              <dd>{gossipCount}</dd>
            </div>
            <div>
              <dt>VDF calibration</dt>
              <dd>
                {calibration
                  ? `${calibration.stepsPerSecond.toFixed(2)} steps/s`
                  : 'Runs before the first rated game'}
              </dd>
            </div>
          </dl>
        </section>

        <section className="panel">
          <p className="eyebrow">Peer configuration</p>
          <h2>Connect another browser</h2>
          <p className="muted">
            Share your peer invite or paste a peer ID to bootstrap a direct
            WebRTC connection.
          </p>
          <label htmlFor="peer-invite">Current peer invite</label>
          <input id="peer-invite" value={inviteUrl} readOnly />
          <button type="button" onClick={copyInvite}>Copy peer invite</button>
          <form className="stack" onSubmit={connectToBootstrap}>
            <label htmlFor="bootstrap">Remote peer ID</label>
            <div className="input-row">
              <input
                id="bootstrap"
                placeholder="dc-…"
                value={bootstrapDraft}
                onChange={(event) => setBootstrapDraft(event.target.value)}
              />
              <button type="submit" className="secondary">Connect</button>
            </div>
          </form>
        </section>

        <section className="panel">
          <div className="section-heading">
            <div>
              <p className="eyebrow">Live presence</p>
              <h2>{remotePeers.length} remote peer{remotePeers.length === 1 ? '' : 's'}</h2>
            </div>
            <span className="counter">{connectionStats.openCount} links</span>
          </div>
          <ul className="peer-list">
            {remotePeers.length === 0 && <li className="empty">No connected peers yet.</li>}
            {remotePeers.map((peer) => (
              <li key={peer.peerId}>
                <span className="avatar" aria-hidden="true">♟</span>
                <span>
                  <strong>{trustedPeerNickname(peer)}</strong>
                  <small>{peer.peerId}</small>
                </span>
              </li>
            ))}
          </ul>
          <button type="button" onClick={sendHello} disabled={!joined}>
            Broadcast signed hello
          </button>
        </section>

        <section className="panel">
          <div className="section-heading">
            <div>
              <p className="eyebrow">Protocol activity</p>
              <h2>{messages.length} hello{messages.length === 1 ? '' : 's'} received</h2>
            </div>
          </div>
          <ul className="message-list" aria-live="polite">
            {messages.length === 0 && <li className="empty">No signed hellos received yet.</li>}
            {[...messages].reverse().slice(0, 24).map((message, index) => (
              <li key={`${message.senderId}-${message.payload?.sentAt || index}-${index}`}>
                <strong>{trustedHelloNickname(message)}</strong>
                <span>said hello</span>
                <small>{message.payload?.sentAt || 'just now'}</small>
              </li>
            ))}
          </ul>
        </section>
      </div>

      <footer>
        <span>{scope}</span>
        <span>{signalingCount} public signaling endpoints</span>
        <span>{notice}</span>
        <ReleaseNote />
      </footer>
    </div>
  );
}